/**
 * @file EmployeePicker.tsx
 * @brief Filter by company employee (scoped list from `/api/company/users`).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 01-10-2026 — `variant="pill"`: the page-header `HeaderFilterMenu`
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, User as UserIcon, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { Avatar } from "@/components/ui/Avatar";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { cn } from "@/shared/theme/cn";
import { formatFullName, getInitials } from "@/shared/utils/formatters";
import { useEmployeesList } from "@/features/employees/api";

interface EmployeePickerProps {
  value?: string | null;
  onChange: (employeeId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Extra classes for the trigger button (e.g. flush filter cells). */
  triggerClassName?: string;
  /** `pill`: the 56px page-header filter beside the date circle. */
  variant?: "field" | "pill";
  /** Pill only: 68px, for the Boardrooms filter row. */
  tall?: boolean;
}

export function EmployeePicker({
  value,
  onChange,
  placeholder = "All employees",
  disabled,
  triggerClassName,
  variant = "field",
  tall = false,
}: EmployeePickerProps) {
  const [open, setOpen] = useState(false);
  const employees = useEmployeesList();

  const selected = useMemo(
    () => employees.data?.find((e) => e.id === value) ?? null,
    [employees.data, value],
  );

  const items: SearchableMenuItem[] = useMemo(() => {
    const rows: SearchableMenuItem[] = [
      {
        value: "__all__",
        label: "All employees",
        description: "Company-wide",
        icon: (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-brand">
            <Users className="h-3.5 w-3.5" aria-hidden />
          </span>
        ),
      },
    ];
    for (const e of employees.data ?? []) {
      const name = formatFullName(e.first_name, e.last_name);
      const label = name !== "—" ? name : e.email;
      rows.push({
        value: e.id,
        label,
        description: e.email,
        keywords: [e.first_name, e.last_name].filter(Boolean).join(" "),
        icon: (
          <Avatar
            src={e.profile_image_url}
            initials={getInitials(e.first_name, e.last_name, e.email)}
            gender={e.gender}
            alt=""
            size="sm"
          />
        ),
      });
    }
    return rows;
  }, [employees.data]);

  const triggerLabel = selected
    ? formatFullName(selected.first_name, selected.last_name) !== "—"
      ? formatFullName(selected.first_name, selected.last_name)
      : selected.email
    : placeholder;

  if (variant === "pill") {
    return (
      <HeaderFilterMenu
        tall={tall}
        label={triggerLabel}
        active={Boolean(selected)}
        onClear={() => onChange(null)}
        icon={
          selected ? (
            <Avatar
              src={selected.profile_image_url}
              initials={getInitials(selected.first_name, selected.last_name, selected.email)}
              gender={selected.gender}
              alt=""
              size="md"
            />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/10 text-brand">
              <UserIcon className="h-4 w-4" />
            </span>
          )
        }
        title="Filter by employee"
        searchPlaceholder="Search employees"
        items={items}
        loading={employees.isLoading}
        disabled={disabled}
        selected={value ?? "__all__"}
        onSelect={(id) => onChange(id === "__all__" ? null : id)}
      />
    );
  }

  return (
    // A dropdown under the button, searchable (DES 30-09-2026: "ab user
    // picker aisa banao"), in place of the side sheet.
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled || employees.isLoading}
          className={cn(
            "inline-flex h-9 w-full min-w-[160px] items-center justify-between gap-2 rounded-full border bg-background px-3 text-sm",
            "hover:bg-muted/50 disabled:opacity-50",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected ? (
              <Avatar
                src={selected.profile_image_url}
                initials={getInitials(selected.first_name, selected.last_name, selected.email)}
                gender={selected.gender}
                alt={triggerLabel}
                size="sm"
              />
            ) : (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                <UserIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-72 p-1.5"
      >
        <SearchableMenuList
          title="Filter by employee"
          searchPlaceholder="Search employees"
          items={items}
          loading={employees.isLoading}
          selected={[value ?? "__all__"]}
          onSelect={(id) => onChange(id === "__all__" ? null : id)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
